import {DateRange} from 'rsuite/DateRangePicker';

import IDateAfterBefore from '../model/IDateAfterBefore';
import IOrderBy from '../model/IOrderBy';

export const TAGS_FILTER_PARAM = 'tags.label[]';

export const ALL_FILTER_VALUE = 'all';

export const SEARCH_PARAM = 'search';

export type UrlFilterValue = string | string[] | undefined;

export type UrlFiltersData = Record<string, UrlFilterValue>;

type DateRangeFilters = Partial<Record<`${string}[after]` | `${string}[before]`, UrlFilterValue>>;

export function getSearchFilters(params: URLSearchParams): string[] {
    const value = params.get(SEARCH_PARAM)?.trim();
    return value ? [`${SEARCH_PARAM}=${encodeURIComponent(value)}`] : [];
}

export const FILTERS_KEPT_PARAMS: readonly string[] = [SEARCH_PARAM, 'order', 'dir'];

export const FILTERS_KEPT_SEARCH: readonly string[] = [SEARCH_PARAM];

export const FILTERS_KEPT_NONE: readonly string[] = [];

export function getPage(params: URLSearchParams): number {
    const page = Number(params.get('page'));
    return Number.isInteger(page) && page > 0 ? page : 1;
}

export function dateFilterKeys(field: string): [`${string}[after]`, `${string}[before]`] {
    return [`${field}[after]` as const, `${field}[before]` as const];
}

export function getUrlFilters(params: URLSearchParams, keys: readonly string[]): string[] {
    return keys.flatMap(key => {
        const value = params.get(key);
        return value ? [`${key}=${encodeURIComponent(value)}`] : [];
    });
}

export function getUrlDateRange(params: URLSearchParams, field: string): DateRange | undefined {
    const [afterKey, beforeKey] = dateFilterKeys(field);
    return stringsToDateRange(params.get(afterKey) ?? undefined, params.get(beforeKey) ?? undefined);
}

export function resetFiltersSearchParams(
    params: URLSearchParams,
    keep: readonly string[] = FILTERS_KEPT_PARAMS,
): URLSearchParams {
    const next = new URLSearchParams();
    keep.forEach(key => {
        const value = params.get(key);
        if (value !== null) {
            next.set(key, value);
        }
    });

    return next;
}

const NUMBER_FILTER_PATTERN = /^\d+(\.\d+)?$/;

export function toNumberFilter(value: string | undefined): string | undefined {
    const normalized = value?.trim().replace(',', '.');
    if (!normalized || !NUMBER_FILTER_PATTERN.test(normalized)) {
        return undefined;
    }

    return String(Number(normalized));
}

function withTagsInSearchParams(params: URLSearchParams, tags: string[]): URLSearchParams {
    const next = new URLSearchParams(params);
    next.delete(TAGS_FILTER_PARAM);
    tags.forEach(item => next.append(TAGS_FILTER_PARAM, item));
    next.set('page', '1');

    return next;
}

export function toggleTagInSearchParams(params: URLSearchParams, tag: string): URLSearchParams {
    const activeTags = params.getAll(TAGS_FILTER_PARAM);

    return withTagsInSearchParams(params, activeTags.includes(tag)
        ? activeTags.filter(item => item !== tag)
        : [...activeTags, tag]);
}

export function removeTagFromSearchParams(params: URLSearchParams, tag: string): URLSearchParams {
    return withTagsInSearchParams(params, params.getAll(TAGS_FILTER_PARAM).filter(item => item !== tag));
}

export function clearTagsInSearchParams(params: URLSearchParams): URLSearchParams {
    return withTagsInSearchParams(params, []);
}

export function tagsToUrlFilters(tags?: string[]): UrlFiltersData {
    return {[TAGS_FILTER_PARAM]: tags ?? []};
}

export function allValueToUndefined(value: string | undefined): string | undefined {
    return value === ALL_FILTER_VALUE ? undefined : value;
}


export function fromUrlToFilters<T, K extends keyof T & string>(params: URLSearchParams, filters: T, field: K) {
    if (!params.has(field)) {
        return;
    }
    const value = params.get(field);
    if (value) {
        filters[field] = <T[K]>value;
    }
}

export function objectToArray(object: object): string[] {
    const result: string[] = [];

    for (const [key, value] of Object.entries(object)) {
        if (value === undefined || value === null || value === '') {
            continue;
        }

        if (key === 'tags' && Array.isArray(value)) {
            value.forEach(tag => {
                result.push(`${TAGS_FILTER_PARAM}=${encodeURIComponent(tag)}`);
            });
        } else if (key === 'type' && Array.isArray(value)) {
            value.forEach(typeItem => {
                result.push(`type[]=${encodeURIComponent(typeItem)}`);
            });
        } else {
            result.push(`${key}=${encodeURIComponent(value)}`);
        }
    }

    return result;
}

export function setSearchParamsFromFilters(params: URLSearchParams, object: object): void {
    params.delete(TAGS_FILTER_PARAM);
    params.delete('type');

    for (const [key, value] of Object.entries(object)) {
        if (value === '' || value === undefined || value === null) {
            params.delete(key);
        } else {
            params.set(key, value);
        }
    }

    if ((object as any).tags && Array.isArray((object as any).tags)) {
        (object as any).tags.forEach((tag: string) => {
            params.append(TAGS_FILTER_PARAM, tag);
        });
    }
}

export function buildFiltersSearchParams(
    current: URLSearchParams,
    filters: object,
    tags?: string[],
): URLSearchParams {
    const data: UrlFiltersData = tagsToUrlFilters(tags);
    Object.entries(filters).forEach(([key, value]) => {
        data[key] = value === undefined || value === null ? undefined : String(value);
    });

    return applyUrlFilters(current, data);
}

export function applyUrlFilters(current: URLSearchParams, filters: UrlFiltersData): URLSearchParams {
    const params = new URLSearchParams(current);

    Object.entries(filters).forEach(([key, value]) => {
        if (Array.isArray(value)) {
            params.delete(key);
            value.forEach(item => item && params.append(key, item));
        } else if (value) {
            params.set(key, value);
        } else {
            params.delete(key);
        }
    });
    params.set('page', '1');

    return params;
}

export function stringsToDateRange(after: string | undefined, before: string | undefined): DateRange | undefined {
    const start = after ? new Date(after) : undefined;
    const end = before ? new Date(before) : undefined;

    if (start === undefined || end === undefined) {
        return undefined;
    }

    return [start, end];
}

export function dateRangeToFilters(dateRange: DateRange | undefined, filters: DateRangeFilters, field: string): void {
    const [afterKey, beforeKey] = dateFilterKeys(field);
    const range = dateRangeToDateAfterBefore(dateRange);

    filters[afterKey] = range?.after;
    filters[beforeKey] = range?.before;
}

export function dateRangeToDateAfterBefore(dateRange: DateRange | undefined): IDateAfterBefore | undefined {

    if (dateRange === undefined || dateRange === null) {
        return undefined;
    }
    const after = new Date(dateRange[0]);
    after.setHours(0, 0, 0, 0);

    const before = new Date(dateRange[1]);
    before.setHours(23, 59, 59);

    return {
        after: after.toISOString(),
        before: before.toISOString(),
    };
}

export function stringToBoolean(value: string | undefined): boolean | undefined{
    if (value === undefined) {
        return undefined;
    }
    return value === 'true';
}

export function booleanToString(value: boolean | undefined): string | undefined{
    if (value === undefined) {
        return undefined;
    }
    return value.toString();
}

export function sortToUrlFilters(sort?: IOrderBy): UrlFiltersData {
    return sort?.field && sort.direction
        ? {order: sort.field, dir: sort.direction}
        : {order: undefined, dir: undefined};
}
